/**
 * Formato de dinero.
 *
 * REGLA DEL PROYECTO (ADR-0003): la API **siempre** habla en centavos enteros.
 * La conversión a pesos ocurre sólo aquí, en el borde de presentación.
 *
 * ¿Por qué? Porque `0.1 + 0.2 !== 0.3` en punto flotante y un POS que descuadra
 * un centavo por venta descuadra miles al mes.
 */

const formatters = new Map<string, Intl.NumberFormat>();

function getFormatter(locale: string, currency: string): Intl.NumberFormat {
  const key = `${locale}:${currency}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    formatters.set(key, formatter);
  }
  return formatter;
}

/** 123456 → "$1,234.56" */
export function formatMoney(cents: number, currency = "MXN", locale = "es-MX"): string {
  return getFormatter(locale, currency).format(cents / 100);
}

/** 123456 → "1,234.56" (sin símbolo, para columnas de tabla ya rotuladas) */
export function formatAmount(cents: number, locale = "es-MX"): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** 16.0 → "16%" — para tasas de impuesto y descuentos. */
export function formatPercent(value: number, locale = "es-MX"): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

/**
 * Convierte lo que escribe un cajero ("1,234.56", "1234.5", "$1 234,56") a
 * centavos. Devuelve `null` si no se puede interpretar, para que la UI muestre
 * un error en vez de guardar un precio silenciosamente equivocado.
 */
export function parseMoneyToCents(input: string): number | null {
  const cleaned = input.replace(/[^\d.,-]/g, "").trim();
  if (cleaned === "") return null;

  // Si hay coma Y punto, el último separador es el decimal.
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let normalized = cleaned;

  if (lastComma > -1 && lastDot > -1) {
    normalized =
      lastComma > lastDot
        ? cleaned.replace(/\./g, "").replace(",", ".")
        : cleaned.replace(/,/g, "");
  } else if (lastComma > -1) {
    // "1234,5" → decimal ; "1,234" → millar (3 dígitos después de la coma)
    const decimals = cleaned.length - lastComma - 1;
    normalized = decimals === 3 ? cleaned.replace(/,/g, "") : cleaned.replace(",", ".");
  }

  const value = Number.parseFloat(normalized);
  if (Number.isNaN(value)) return null;

  // Se redondea al centavo: nunca se guarda medio centavo.
  return Math.round(value * 100);
}

/**
 * Convierte lo que escribe un cajero a centavos.
 *
 * Acepta "150", "150.50" y "150,50". Escribir "150" en una caja significa
 * ciento cincuenta pesos, no un peso con cincuenta: el atajo más usado del POS
 * no puede exigir el punto decimal.
 *
 * Devuelve `0` ante cualquier cosa que no sea un número, en vez de `NaN`: un
 * `NaN` que llega a un importe se propaga hasta el ticket.
 */
export function parseAmountInput(input: string): number {
  const clean = input.trim().replace(",", ".");
  if (!clean || !/^\d*\.?\d*$/.test(clean)) return 0;
  const value = Number.parseFloat(clean);
  if (Number.isNaN(value)) return 0;
  return Math.round(value * 100);
}
